import type { SessionExercise } from '@cadencia/contracts';
import { findSubstitutions, requireExercise, type SubstitutionCandidate } from '@cadencia/domain';
import { useEffect, useState, type ReactNode } from 'react';
import { Boton, Chip } from '../../components/ui.js';
import { useAppData } from '../../data/DataProvider.js';
import { approveDecision, saveDecisions } from '../../data/repositories/decisionsRepo.js';
import { getDefaultGym, listEquipment } from '../../data/repositories/gymRepo.js';
import { applyDecisions } from '../../data/services/applyDecisionsService.js';
import { nowIso } from '../../data/ids.js';
import { proposalFromSubstitution } from '@cadencia/domain';
import './entrenar.css';
import { mensajeDeError } from '../../lib/errores.js';

/**
 * Sustituir ejercicio (requisito §8.2, docs/TRAINING_ENGINE.md §7.2).
 *
 * Las alternativas salen del motor de sustituciones: patron de movimiento,
 * musculos, perfil de resistencia y region de la curva, filtrando por el
 * equipamiento que hay de verdad. **No** es una coincidencia por grupo
 * muscular, y cada candidato viene con su motivo escrito.
 *
 * El cambio se registra como decision antes de tocar el plan: elegir aqui es
 * una decision del usuario y queda en el historial como tal.
 *
 * Sirve a las DOS pantallas que el §7.2 nombra: la previsualizacion y la sesion
 * en curso. Lo que cambia entre ellas no es el motor, son los datos que se le
 * pueden dar —en la sesion ya hay readiness del dia y puede haber series
 * registradas en el hueco—, asi que entran por parametro y no se adivinan aqui.
 */
export function SustituirEjercicio({
  sessionExercise,
  readinessScore,
  seriesRegistradas = 0,
  onHecho,
}: {
  sessionExercise: SessionExercise;
  /**
   * Readiness de hoy, 0-100. El §7.2 lo usa para penalizar la demanda tecnica:
   * un dia malo no es el dia de estrenar un ejercicio dificil. Solo lo pasa
   * quien lo tiene de verdad; la previsualizacion de una sesion futura no.
   */
  readinessScore?: number | undefined;
  /** Series ya registradas en este hueco hoy. Cambia lo que hay que advertir. */
  seriesRegistradas?: number | undefined;
  onHecho: () => void;
}): ReactNode {
  const { ctx, refreshPending } = useAppData();
  const [candidatos, setCandidatos] = useState<SubstitutionCandidate[] | null>(null);
  const [aplicando, setAplicando] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const original = requireExercise(sessionExercise.exerciseSlug);

  useEffect(() => {
    if (!ctx) return;
    void (async () => {
      try {
        const gimnasio = await getDefaultGym(ctx.userId);
        const equipamiento = gimnasio ? await listEquipment(gimnasio.id) : [];
        setCandidatos(
          findSubstitutions(sessionExercise.exerciseSlug, {
            availableKinds: [...new Set(equipamiento.map((equipo) => equipo.kind))],
            // Se compone con spread y no con `readinessScore` a secas:
            // `exactOptionalPropertyTypes` distingue «ausente» de «presente y
            // undefined», y el contexto del dominio declara el campo opcional.
            ...(readinessScore === undefined ? {} : { readinessScore }),
          }),
        );
      } catch (caught) {
        setError(mensajeDeError(caught));
      }
    })();
  }, [ctx, sessionExercise.exerciseSlug, readinessScore]);

  if (!ctx) return null;

  const elegir = (candidato: SubstitutionCandidate): void => {
    setAplicando(candidato.slug);
    setError(null);
    void (async () => {
      try {
        const propuesta = proposalFromSubstitution(
          sessionExercise.exerciseSlug,
          candidato,
          nowIso(),
        );
        // Se acota al hueco concreto: sustituir aqui no cambia el resto del
        // bloque a espaldas del usuario.
        const [guardada] = await saveDecisions(ctx, [
          {
            ...propuesta,
            inputs: { ...propuesta.inputs, sessionExerciseId: sessionExercise.id },
          },
        ]);
        if (guardada === undefined) throw new Error('no se pudo registrar la decisión');

        // Elegir en esta pantalla ES la aprobacion.
        await approveDecision(ctx, guardada);
        const resultado = await applyDecisions(ctx);
        await refreshPending();

        if (resultado.applied.length === 0) {
          setError(resultado.skipped[0]?.reason ?? 'no se pudo aplicar la sustitución');
          return;
        }
        onHecho();
      } catch (caught) {
        setError(mensajeDeError(caught));
      } finally {
        setAplicando(null);
      }
    })();
  };

  return (
    <section className="sustituir" aria-label={`Alternativas a ${original.nameEs}`}>
      <p className="sustituir__titulo">Alternativas a {original.nameEs}</p>

      {/* El alcance, escrito. Cambiar aqui toca ESTE hueco y nada mas, y quien
          lo hace porque la maquina esta ocupada no quiere perder el ejercicio
          en las tres semanas que quedan de bloque. Si no se dice, la unica
          forma de averiguarlo es mirar el plan de la semana que viene. */}
      <p className="campo__pista">
        Solo cambia esta sesión. El resto del bloque sigue con {original.nameEs}.
      </p>

      {seriesRegistradas > 0 && (
        <p className="campo__pista">
          {seriesRegistradas === 1
            ? 'La serie que ya has registrado se queda como está'
            : `Las ${String(seriesRegistradas)} series que ya has registrado se quedan como están`}
          : el cambio solo afecta a las que quedan.
        </p>
      )}

      {candidatos === null && <p className="campo__pista">Buscando alternativas…</p>}

      {candidatos !== null && candidatos.length === 0 && (
        <p className="campo__pista">
          No hay ninguna alternativa razonable con el equipamiento que tienes declarado.
        </p>
      )}

      {(candidatos ?? []).map((candidato) => (
        <div key={candidato.slug} className="sustituir__opcion">
          <div className="sustituir__texto">
            <p className="sustituir__nombre">{candidato.nameEs}</p>
            <p className="campo__pista">{candidato.reason}</p>
          </div>
          <div className="sustituir__acciones">
            <Chip tono={candidato.change.level === 'minor' ? 'verde' : 'aviso'}>
              {candidato.change.level === 'minor' ? 'Estímulo equivalente' : 'Cambia el estímulo'}
            </Chip>
            <Boton
              variante="secundario"
              onClick={() => elegir(candidato)}
              disabled={aplicando !== null}
            >
              {aplicando === candidato.slug ? 'Cambiando…' : 'Usar este'}
            </Boton>
          </div>
        </div>
      ))}

      {error !== null && (
        <p className="campo__error" role="alert">
          {error}
        </p>
      )}

      <Boton variante="texto" onClick={onHecho}>
        Cancelar
      </Boton>
    </section>
  );
}
